import {ColumnKind, TableSpec} from '../table/model';
import {CubeMeasure} from '../types';
import {AnswersColumn, AnswersData} from './types';

const columnId = (position: number): string => `c${position}`;

function columnKind(kind: AnswersColumn['kind']): ColumnKind {
    return kind === 'label' ? 'plain' : kind === 'number' ? 'value' : kind;
}

/** What formats a number: the table's value cells take a measure's precision and unit */
function measureOf(column: AnswersColumn, position: number): CubeMeasure {
    return {
        id: columnId(position),
        label: column.header,
        metric: 'value',
        precision: column.precision ?? 0,
        ...(column.unit ? {unit: column.unit} : {}),
    };
}

/** The page as a table: server order, no viewer sorting (a sort of one page would not be the order of the answers) */
export function answersTable(data: AnswersData): TableSpec {
    return {
        sortable: false,
        columns: data.columns.map((column, position) => ({
            id: columnId(position),
            // Context columns are values of the row, not its header
            kind: columnKind(column.kind),
            label: column.header,
            ...(column.kind === 'number' ? {measure: measureOf(column, position)} : {}),
            // The date stays in view while a wide table scrolls; only a leading run can stick
            sticky: position === 0 && column.kind === 'date',
        })),
        rows: data.rows.map(row => ({
            key: String(row.id),
            role: 'data',
            cells: Object.fromEntries(row.cells.map((value, position) => [columnId(position), {value}])),
        })),
    };
}

/** Id of the table column that holds the answer text */
export function answerColumnId(data: AnswersData): string | undefined {
    const position = data.columns.findIndex(column => column.source === 'answer');

    return position < 0 ? undefined : columnId(position);
}
